import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, RotateCcw, Users, Grid3X3, Shuffle, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { StudentAvatar } from '../components/StudentAvatar';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const SORT_OPTIONS = [
  { value: 'alphabetical', label: 'A-Z (Alphabetical)' },
  { value: 'reverse', label: 'Z-A (Reverse)' },
  { value: 'random', label: 'Random Shuffle' },
  { value: 'first_name', label: 'First Name A-Z' },
  { value: 'last_name', label: 'Last Name A-Z' },
];

const GROUP_OPTIONS = [
  { value: '1', label: 'Individual Seats' },
  { value: '2', label: 'Pairs (Groups of 2)' },
  { value: '3', label: 'Groups of 3' },
  { value: '4', label: 'Groups of 4' },
  { value: '5', label: 'Groups of 5' },
  { value: '6', label: 'Groups of 6' },
];

const LAYOUT_OPTIONS = [
  { value: 'grid', label: 'Grid Layout' },
  { value: 'rows', label: 'Rows (Classroom Style)' },
  { value: 'u-shape', label: 'U-Shape' },
  { value: 'circle', label: 'Circle / Oval' },
];

export const SeatingPlanPage = () => {
  const { classId } = useParams();
  const containerRef = useRef(null);
  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [positions, setPositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [arrangeOpen, setArrangeOpen] = useState(false);

  // Arrange options
  const [rows, setRows] = useState(5);
  const [cols, setCols] = useState(6);
  const [groupSize, setGroupSize] = useState('1');
  const [sortOrder, setSortOrder] = useState('alphabetical');
  const [layoutType, setLayoutType] = useState('grid');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studentsRes, classesRes, seatingRes] = await Promise.all([
          fetch(`${API}/students/${classId}`, { credentials: 'include' }),
          fetch(`${API}/classes`, { credentials: 'include' }),
          fetch(`${API}/seating-plan/${classId}`, { credentials: 'include' })
        ]);
        if (studentsRes.ok) setStudents(await studentsRes.json());
        if (classesRes.ok) {
          const classes = await classesRes.json();
          setClassInfo(classes.find(c => c.class_id === classId));
        }
        if (seatingRes.ok) {
          const seats = await seatingRes.json();
          const posMap = {};
          seats.forEach(s => { posMap[s.student_id] = { x: s.x, y: s.y }; });
          setPositions(posMap);
        }
      } catch (error) { console.error('Failed to fetch:', error); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [classId]);

  // Auto-place unpositioned students
  useEffect(() => {
    if (!loading && students.length > 0) {
      setPositions(prev => {
        const updated = { ...prev };
        let nextIndex = Object.keys(updated).length;
        students.forEach(s => {
          if (!updated[s.student_id]) {
            const col = nextIndex % 6;
            const row = Math.floor(nextIndex / 6);
            updated[s.student_id] = { x: 80 + col * 120, y: 120 + row * 100 };
            nextIndex++;
          }
        });
        return updated;
      });
    }
  }, [loading, students]);

  const getCoords = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const handleDragStart = (e, studentId) => {
    e.preventDefault();
    const coords = getCoords(e);
    const pos = positions[studentId] || { x: 0, y: 0 };
    setDragOffset({ x: coords.x - pos.x, y: coords.y - pos.y });
    setDragging(studentId);
  };

  const handleDragMove = useCallback((e) => {
    if (!dragging) return;
    e.preventDefault();
    const coords = getCoords(e);
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(30, Math.min(rect.width - 30, coords.x - dragOffset.x));
    const y = Math.max(30, Math.min(rect.height - 30, coords.y - dragOffset.y));
    setPositions(prev => ({ ...prev, [dragging]: { x, y } }));
  }, [dragging, dragOffset, getCoords]);

  const handleDragEnd = useCallback(() => { setDragging(null); }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [dragging, handleDragMove, handleDragEnd]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const positionsArr = Object.entries(positions).map(([student_id, pos]) => ({
        student_id, x: pos.x, y: pos.y
      }));
      const r = await fetch(`${API}/seating-plan/${classId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ positions: positionsArr })
      });
      if (r.ok) toast.success('Seating plan saved');
      else toast.error('Failed to save');
    } catch (error) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleExportPDF = async () => {
    toast.info('Generating PDF...');
    try {
      const { default: html2pdf } = await import('html2pdf.js');
      const container = containerRef.current;
      if (!container) return;
      const clone = container.cloneNode(true);
      clone.style.width = `${container.clientWidth}px`;
      clone.style.height = `${container.clientHeight}px`;
      clone.style.position = 'relative';
      clone.style.overflow = 'visible';
      document.body.appendChild(clone);
      await html2pdf().set({
        margin: 10,
        filename: `seating-plan-${classInfo?.name || 'class'}-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'px', format: [container.clientWidth + 20, container.clientHeight + 20], orientation: 'landscape' },
      }).from(clone).save();
      document.body.removeChild(clone);
      toast.success('Seating plan exported as PDF');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Failed to export PDF');
    }
  };

  // Sort students
  const sortStudents = (studentList) => {
    const sorted = [...studentList];
    switch (sortOrder) {
      case 'alphabetical': return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'reverse': return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'random': return sorted.sort(() => Math.random() - 0.5);
      case 'first_name': return sorted.sort((a, b) => a.name.split(' ')[0].localeCompare(b.name.split(' ')[0]));
      case 'last_name': return sorted.sort((a, b) => {
        const aLast = a.name.split(' ').pop();
        const bLast = b.name.split(' ').pop();
        return aLast.localeCompare(bLast);
      });
      default: return sorted;
    }
  };

  // Auto-arrange with layout
  const handleAutoArrange = () => {
    const sorted = sortStudents(students);
    const rect = containerRef.current?.getBoundingClientRect();
    const canvasW = rect?.width || 800;
    const canvasH = rect?.height || 600;
    const padX = 60, padY = 50;
    const usableW = canvasW - padX * 2;
    const usableH = canvasH - padY * 2 - 40; // 40px for front-of-class label at bottom
    const group = parseInt(groupSize);
    const updated = {};

    if (layoutType === 'grid' || layoutType === 'rows') {
      const effectiveCols = Math.min(cols, Math.ceil(sorted.length / (group > 1 ? 1 : 1)));
      const groupGap = group > 1 ? 20 : 0;
      const numGroups = Math.ceil(sorted.length / group);
      const groupsPerRow = Math.min(effectiveCols, numGroups);
      const numRows = Math.ceil(numGroups / groupsPerRow);

      // Calculate spacing
      const cellW = group > 1 ? (usableW - (groupsPerRow - 1) * groupGap) / groupsPerRow : usableW / effectiveCols;
      const cellH = usableH / Math.max(numRows, rows);

      sorted.forEach((s, i) => {
        const groupIdx = Math.floor(i / group);
        const posInGroup = i % group;
        const gRow = Math.floor(groupIdx / groupsPerRow);
        const gCol = groupIdx % groupsPerRow;

        let x, y;
        if (group > 1) {
          // Within group: side by side
          const groupX = padX + gCol * (cellW + groupGap) + cellW / 2;
          const subOffset = (posInGroup - (group - 1) / 2) * 55;
          x = groupX + subOffset;
          y = padY + gRow * cellH + cellH / 2;
        } else {
          x = padX + gCol * cellW + cellW / 2;
          y = padY + gRow * cellH + cellH / 2;
        }
        updated[s.student_id] = { x: Math.max(30, Math.min(canvasW - 30, x)), y: Math.max(30, Math.min(canvasH - 70, y)) };
      });
    } else if (layoutType === 'u-shape') {
      const total = sorted.length;
      const sideCount = Math.floor(total / 3);
      const bottomCount = total - sideCount * 2;
      let idx = 0;

      // Left side (top to bottom, but remember screen is flipped: back is top, front is bottom)
      for (let i = 0; i < sideCount && idx < total; i++, idx++) {
        updated[sorted[idx].student_id] = {
          x: padX + 40,
          y: padY + (i / Math.max(sideCount - 1, 1)) * usableH
        };
      }
      // Bottom (front of class) - left to right
      for (let i = 0; i < bottomCount && idx < total; i++, idx++) {
        updated[sorted[idx].student_id] = {
          x: padX + 80 + (i / Math.max(bottomCount - 1, 1)) * (usableW - 160),
          y: canvasH - padY - 50
        };
      }
      // Right side (bottom to top)
      for (let i = 0; i < sideCount && idx < total; i++, idx++) {
        updated[sorted[idx].student_id] = {
          x: canvasW - padX - 40,
          y: padY + usableH - (i / Math.max(sideCount - 1, 1)) * usableH
        };
      }
    } else if (layoutType === 'circle') {
      const total = sorted.length;
      const cx = canvasW / 2;
      const cy = (canvasH - 40) / 2;
      const rx = usableW / 2 - 40;
      const ry = usableH / 2 - 20;

      sorted.forEach((s, i) => {
        const angle = (2 * Math.PI * i) / total - Math.PI / 2;
        updated[s.student_id] = {
          x: cx + rx * Math.cos(angle),
          y: cy + ry * Math.sin(angle)
        };
      });
    }

    setPositions(updated);
    setArrangeOpen(false);
    toast.success('Students arranged');
  };

  const breadcrumb = [
    { label: 'Teach Now', path: '/app/live-mode' },
    { label: classInfo?.name || 'Class', path: '/app/live-mode' },
    { label: 'Seating Plan' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="flex flex-col h-[calc(100vh-120px)]" data-testid="seating-plan-page">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-heading font-semibold text-slate-900">Seating Plan</h1>
            <p className="text-sm text-slate-500">{classInfo?.name} — Drag students to arrange</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setArrangeOpen(true)} className="border-slate-200 rounded-xl text-sm" data-testid="auto-arrange-btn">
              <Grid3X3 className="w-4 h-4 mr-1.5" />Auto Arrange
            </Button>
            <Button variant="outline" onClick={handleExportPDF} className="border-slate-200 rounded-xl text-sm" data-testid="export-pdf-btn">
              <FileDown className="w-4 h-4 mr-1.5" />Export PDF
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm" data-testid="save-seating-btn">
              <Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        <div
          ref={containerRef}
          className="flex-1 bg-white rounded-xl border border-slate-200 relative overflow-hidden select-none"
          style={{ cursor: dragging ? 'grabbing' : 'default', minHeight: '400px' }}
          data-testid="seating-canvas"
        >
          {/* Grid dots */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-10">
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="1.5" fill="#94a3b8" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>

          {/* Back of class - TOP */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 px-5 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs text-slate-400 font-medium" data-testid="back-of-class-label">
            Back of Class
          </div>

          {/* Front of class - BOTTOM */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-5 py-1.5 bg-teal-100 border border-teal-200 rounded-lg text-xs text-teal-600 font-medium" data-testid="front-of-class-label">
            Front of Class (Teacher)
          </div>

          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center text-slate-400">Loading...</div>
          ) : students.length === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <Users className="w-10 h-10 text-slate-200 mb-3" />
              <p className="text-sm text-slate-400">Add students to create a seating plan</p>
            </div>
          ) : (
            students.map((student, index) => {
              const pos = positions[student.student_id] || { x: 50, y: 50 };
              const isDragging = dragging === student.student_id;

              return (
                <div
                  key={student.student_id}
                  className={`absolute flex flex-col items-center select-none transition-shadow ${isDragging ? 'z-50 scale-110' : 'z-10'}`}
                  style={{
                    left: pos.x,
                    top: pos.y,
                    transform: 'translate(-50%, -50%)',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    transition: isDragging ? 'none' : 'box-shadow 0.2s',
                  }}
                  onMouseDown={(e) => handleDragStart(e, student.student_id)}
                  onTouchStart={(e) => handleDragStart(e, student.student_id)}
                  data-testid={`seat-${student.student_id}`}
                >
                  <StudentAvatar student={student} size="md" className={`shadow-md ${isDragging ? 'ring-4 ring-teal-200' : ''}`} />
                  <span className="text-[11px] font-medium text-slate-700 mt-1 max-w-[80px] truncate text-center bg-white/90 px-1.5 rounded">
                    {student.name}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Auto Arrange Dialog */}
      <Dialog open={arrangeOpen} onOpenChange={setArrangeOpen}>
        <DialogContent className="bg-white rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-slate-900 flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-teal-500" /> Auto Arrange
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Layout */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Layout Style</label>
              <Select value={layoutType} onValueChange={setLayoutType}>
                <SelectTrigger className="border-slate-200 rounded-xl" data-testid="layout-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LAYOUT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Grouping */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Student Grouping</label>
              <Select value={groupSize} onValueChange={setGroupSize}>
                <SelectTrigger className="border-slate-200 rounded-xl" data-testid="group-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GROUP_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Sort order */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Sort Order</label>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="border-slate-200 rounded-xl" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Rows & Cols (for grid/rows only) */}
            {(layoutType === 'grid' || layoutType === 'rows') && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Max Rows</label>
                  <Input
                    type="number" min="1" max="10" value={rows}
                    onChange={(e) => setRows(Math.max(1, parseInt(e.target.value) || 1))}
                    className="border-slate-200 rounded-xl text-center"
                    data-testid="rows-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Max Columns</label>
                  <Input
                    type="number" min="1" max="12" value={cols}
                    onChange={(e) => setCols(Math.max(1, parseInt(e.target.value) || 1))}
                    className="border-slate-200 rounded-xl text-center"
                    data-testid="cols-input"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button onClick={handleAutoArrange} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-xl" data-testid="apply-arrange-btn">
                <Grid3X3 className="w-4 h-4 mr-1.5" /> Apply Layout
              </Button>
              <Button
                variant="outline"
                onClick={() => { setSortOrder('random'); setTimeout(handleAutoArrange, 50); }}
                className="border-slate-200 rounded-xl"
                data-testid="shuffle-btn"
              >
                <Shuffle className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
